import { originalText, prepOriginal } from "./audio/prep";
import { decodeFile, duration, makeTone, TONES, type AudioSource, type ToneId } from "./audio/source";
import type { EngineOutput } from "./engine";
import { download, laserZip, meshToStl, toWav } from "./export/exporters";
import type { GenResult, LaserResult, MeshResult, Stat } from "./geom/types";
import { defaults, MODES, type Algo, type Mode, type Values } from "./params";
import { readTrack, type TrackReading } from "./sim/needle";
import { buildControls } from "./ui/controls";
import { FINDINGS, renderLearn, type Finding } from "./ui/learn";
import { Player, type Clip } from "./ui/player";
import { Preview2D } from "./ui/preview2d";
import { drawSpectrum } from "./ui/spectrum";
import type { View3D } from "./ui/view3d";
import type { WorkerRequest, WorkerResponse } from "./worker";
import { LivePanel } from "./live/live";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const COLORS = { original: "#ff5a4f", improved: "#3fa7ff", source: "#b8bec8", prepped: "#9a7dff" };
const DRAW = { original: "#d0342c", improved: "#1f6fd1" };

// ---------- state ----------
let mode: Mode = "spiral";
const values: Record<Mode, Values> = { spiral: defaults("spiral"), linear: defaults("linear"), print3d: defaults("print3d") };
let toneId: ToneId | "file" = "sweep";
let source: AudioSource = makeTone("sweep", 10);
let view: Algo | "both" = "improved";
let last: EngineOutput | null = null;
let lastMode: Mode | null = null;
let latheShown = false;

const worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
let reqId = 0, busy = false, dirty = false;

const preview = new Preview2D($<HTMLCanvasElement>("canvas2d"));
let view3d: View3D | null = null;
let view3dLoading = false;
const player = new Player();

// ---------- worker ----------
function compute() {
  if (busy) { dirty = true; return; }
  busy = true;
  dirty = false;
  setStatus("computing…", true);
  const req: WorkerRequest = { id: ++reqId, mode, values: { ...values[mode] }, source };
  worker.postMessage(req);
}

let debounce = 0;
function scheduleCompute() {
  clearTimeout(debounce);
  debounce = window.setTimeout(compute, 200);
}

worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
  busy = false;
  const msg = e.data;
  if (!msg.ok) {
    setStatus(`error: ${msg.error.split("\n")[0]}`);
    console.error(msg.error);
  } else {
    const refit = lastMode !== msg.out.mode || last === null;
    last = msg.out;
    lastMode = msg.out.mode;
    readings.clear();
    setStatus(`computed in ${(msg.ms / 1000).toFixed(2)} s`);
    render(refit);
  }
  if (dirty) compute();
};

function setStatus(s: string, isBusy = false) {
  const el = $("status");
  el.textContent = s;
  el.classList.toggle("busy", isBusy);
}

// ---------- rendering ----------
function shown(): Algo[] {
  return view === "both" ? ["original", "improved"] : [view];
}

function render(refit = false) {
  if (!last || last.mode !== mode) return;
  const out = last;
  const is3d = mode === "print3d";
  $("canvas2d").hidden = is3d || latheShown;
  $("view3d").hidden = !is3d || latheShown;
  $("latheCanvas").hidden = !latheShown;
  $("exagWrap").hidden = !is3d;
  $("bothBtn").hidden = is3d;
  if (is3d && view === "both") setView("improved");

  if (is3d) {
    if (!view3d) {
      // three.js is only needed for 3D mode: load it on first use (keeps the phone download small)
      if (!view3dLoading) {
        view3dLoading = true;
        void import("./ui/view3d").then(({ View3D }) => {
          view3d = new View3D($("view3d"));
          view3d.onExaggeration = (x) => {
            $<HTMLInputElement>("exag").value = String(x);
            $("exagVal").textContent = `${x}×`;
          };
          render(true);
        });
      }
    } else {
      view3d.start();
      const r = out[view === "both" ? "improved" : view].result as MeshResult | undefined;
      if (r) view3d.setMesh(r, refit);
    }
  } else {
    view3d?.stop();
    const layers = shown()
      .map((a) => ({ res: out[a].result as LaserResult, color: DRAW[a] }))
      .filter((l) => l.res);
    preview.setLayers(layers, refit);
  }
  renderStats(out);
  renderPlayer(out);
  renderSpectrum(out);
  renderExports(out);
}

function renderStats(out: EngineOutput) {
  const o = out.original.result, i = out.improved.result;
  const labels: string[] = [];
  const add = (s?: Stat[]) => s?.forEach((x) => { if (!labels.includes(x.label)) labels.push(x.label); });
  add(o?.stats);
  add(i?.stats);
  const cell = (r: GenResult | undefined, err: string | undefined, label: string) => {
    if (err) return `<td class="bad">error</td>`;
    const s = r?.stats.find((x) => x.label === label);
    if (!s) return "<td class='muted'>—</td>";
    const cls = s.bad ? "bad" : s.good ? "good" : "";
    return `<td class="${cls}" title="${s.help ?? ""}">${s.value}</td>`;
  };
  const rows = labels.map((l) => `<tr><td>${l}</td>${cell(o, out.original.error, l)}${cell(i, out.improved.error, l)}</tr>`);
  $("stats").innerHTML =
    `<tr><th></th><th class="o">Original</th><th class="i">Improved</th></tr>` +
    `<tr><td>Source</td><td colspan="2">${source.name} · ${duration(source).toFixed(1)} s · dominant ${out.sourceDominantHz.toFixed(1)} Hz</td></tr>` +
    rows.join("") +
    `<tr><td>Compute time</td><td>${out.original.ms.toFixed(0)} ms</td><td>${out.improved.ms.toFixed(0)} ms</td></tr>`;
  const w: string[] = [];
  if (out.original.error) w.push(`<div class="w o err">Original failed: ${out.original.error}</div>`);
  if (out.improved.error) w.push(`<div class="w i err">Improved failed: ${out.improved.error}</div>`);
  o?.warnings.forEach((x) => w.push(`<div class="w o"><b>Original:</b> ${x}</div>`));
  i?.warnings.forEach((x) => w.push(`<div class="w i"><b>Improved:</b> ${x}</div>`));
  $("warnings").innerHTML = w.join("");
}

function clips(out: EngineOutput): Clip[] {
  const c: Clip[] = [
    { id: "source", label: "Source audio", color: COLORS.source, channels: source.channels, sampleRate: source.sampleRate },
    { id: "prepped", label: "Improved input (after prep)", color: COLORS.prepped, channels: [out.prepped], sampleRate: out.sampleRate },
  ];
  for (const a of ["original", "improved"] as const) {
    const s = out[a].sim;
    if (s) c.push({ id: a, label: `Needle · ${a}`, color: COLORS[a], channels: s.channels, sampleRate: s.sampleRate });
  }
  return c;
}

function renderPlayer(out: EngineOutput) {
  player.stop();
  const root = $("player");
  root.innerHTML = "";
  for (const clip of clips(out)) {
    const b = document.createElement("button");
    b.className = "play";
    b.dataset.id = clip.id;
    const secs = clip.channels[0].length / clip.sampleRate;
    b.innerHTML = `<span class="dot" style="background:${clip.color}"></span><span class="lbl">▶ ${clip.label}</span><span class="muted small" style="margin-left:auto">${secs.toFixed(1)} s</span>`;
    b.onclick = () => player.toggle(clip);
    const dl = document.createElement("button");
    dl.textContent = "WAV";
    dl.title = "Download as WAV";
    dl.onclick = () => download(toWav(clip.channels, clip.sampleRate), `${source.name}-${mode}-${clip.id}.wav`, "audio/wav");
    root.append(b, dl);
  }
}

player.onChange = (id) => {
  document.querySelectorAll<HTMLButtonElement>("#player button.play").forEach((b) => {
    const on = b.dataset.id === id;
    b.classList.toggle("playing", on);
    b.querySelector(".lbl")!.textContent = `${on ? "■" : "▶"} ${b.querySelector(".lbl")!.textContent!.slice(2)}`;
  });
  if (id === "original" || id === "improved") animatePlayhead(id);
  else { preview.playhead = null; preview.draw(); }
};

// playhead: map needle time → vertex on the displayed groove
const readings = new Map<Algo, TrackReading>();
function animatePlayhead(algo: Algo) {
  const res = last?.[algo].result;
  if (!res || res.kind !== "laser" || mode === "print3d") return;
  const tr = res.track;
  if (!readings.has(algo)) readings.set(algo, readTrack(tr));
  const rd = readings.get(algo)!;
  const step = () => {
    const pos = player.position();
    if (player.playing !== algo || pos === null) { preview.playhead = null; preview.draw(); return; }
    if (tr.kind === "polar" && rd.idx) {
      let lo = 0, hi = rd.n - 1;
      while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (rd.t[mid] <= pos) lo = mid; else hi = mid - 1; }
      const i = rd.idx[lo];
      preview.playhead = { x: tr.xy[2 * i], y: tr.xy[2 * i + 1] };
    } else if (tr.kind === "linear") {
      let t = pos;
      for (let r = 0; r < tr.rows.length; r++) {
        const row = tr.rows[r], len = (row[row.length - 2] - row[0]) / tr.speed;
        if (t <= len || r === tr.rows.length - 1) { preview.playhead = { x: row[0] + t * tr.speed, y: tr.centers[r] }; break; }
        t -= len;
      }
    }
    preview.draw();
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function renderSpectrum(out: EngineOutput) {
  const lines = [{ db: out.sourceSpectrum, color: COLORS.source, label: "source" }];
  const marks: { hz: number; color: string; label: string }[] = [];
  for (const a of ["original", "improved"] as const) {
    const r = out[a];
    if (r.spectrum) lines.push({ db: r.spectrum, color: COLORS[a], label: `needle · ${a}` });
    const res = r.result;
    if (res?.kind === "laser" && res.bandwidth) marks.push({ hz: res.bandwidth[1] / 2, color: COLORS[a], label: "nyquist" });
  }
  if (mode === "print3d") {
    const v = values.print3d;
    marks.push({ hz: 44100 / (v.rateDivisor as number) / 2, color: COLORS.original, label: "nyquist" });
  }
  drawSpectrum($<HTMLCanvasElement>("spectrum"), lines, out.sampleRate, marks);
}

function renderExports(out: EngineOutput) {
  const algo: Algo = view === "both" ? "improved" : view;
  $("exportAlgo").textContent = `— ${algo}`;
  const root = $("exports");
  root.innerHTML = "";
  const btn = (label: string, fn: () => void) => {
    const b = document.createElement("button");
    b.textContent = label;
    b.onclick = () => {
      setStatus(`exporting ${label}…`, true);
      setTimeout(() => { try { fn(); setStatus("exported"); } catch (e) { setStatus(`export failed: ${e}`); } }, 30);
    };
    root.appendChild(b);
  };
  const res = out[algo].result;
  const base = `${source.name}-${algo}`;
  if (res?.kind === "laser") {
    btn(`SVG (${res.files.length} file${res.files.length > 1 ? "s" : ""}, zip)`, async () => download(await laserZip(res, base, "svg"), `${base}-svg.zip`, "application/zip"));
    btn("PDF (zip)", async () => download(await laserZip(res, base, "pdf"), `${base}-pdf.zip`, "application/zip"));
  } else if (res?.kind === "mesh") {
    btn(`STL (${res.stlUnitLabel})`, () => download(meshToStl(res), `${base}.stl`, "model/stl"));
  }
  btn("Audio as .txt (wavtotext format)", () => download(originalText(prepOriginal(source)), `${source.name}.txt`, "text/plain"));
}

// ---------- UI wiring ----------
function setMode(m: Mode) {
  mode = m;
  document.querySelectorAll<HTMLButtonElement>("#modes button").forEach((b) => b.classList.toggle("on", b.dataset.m === m));
  $("modeBlurb").textContent = MODES[m].blurb;
  buildControls($("params"), mode, values[mode], () => { scheduleCompute(); live.refresh(); });
  live.refresh();
  player.stop();
  compute();
}

function setView(v: Algo | "both") {
  view = v;
  document.querySelectorAll<HTMLButtonElement>("#viewAlgo button").forEach((b) => b.classList.toggle("on", b.dataset.v === v));
  render(false);
}

const modesNav = $("modes");
modesNav.className = "seg";
for (const [m, info] of Object.entries(MODES)) {
  const b = document.createElement("button");
  b.textContent = info.label;
  b.dataset.m = m;
  b.onclick = () => setMode(m as Mode);
  modesNav.appendChild(b);
}

document.querySelectorAll<HTMLButtonElement>("#viewAlgo button").forEach((b) => (b.onclick = () => setView(b.dataset.v as Algo | "both")));
document.querySelectorAll<HTMLButtonElement>("#zoomBtns button").forEach((b) => (b.onclick = () => {
  const z = b.dataset.z;
  if (mode === "print3d") z === "fit" ? view3d?.fit() : view3d?.grooveCloseUp();
  else z === "fit" ? preview.fit() : preview.grooveCloseUp();
}));
$<HTMLInputElement>("exag").oninput = (e) => {
  const x = Number((e.target as HTMLInputElement).value);
  $("exagVal").textContent = `${x}×`;
  view3d?.setExaggeration(x);
};
$<HTMLInputElement>("monoSum").onchange = (e) => {
  player.monoSum = (e.target as HTMLInputElement).checked;
  if (player.playing && last) {
    const c = clips(last).find((x) => x.id === player.playing)!;
    player.stop();
    player.toggle(c);
  }
};

preview.onHover = (x, y) => {
  const res = last?.improved.result;
  let extra = "";
  if (res?.kind === "laser" && res.track.kind === "polar") {
    const t = view === "original" && last?.original.result?.track.kind === "polar" ? last.original.result.track : res.track;
    if (t.kind === "polar") extra = ` · r = ${Math.hypot(x - t.cx, y - t.cy).toFixed(4)}″`;
  }
  $("hover").textContent = `x ${x.toFixed(4)}″ y ${y.toFixed(4)}″${extra}`;
};

// source selection
const toneSel = $<HTMLSelectElement>("tone");
for (const [id, label] of Object.entries(TONES)) toneSel.add(new Option(label, id));
toneSel.add(new Option("— loaded file —", "file"));
toneSel.value = toneId;
const toneLen = $<HTMLInputElement>("toneLen");
let fileSource: AudioSource | null = null;
function setTone(id: ToneId | "file", seconds?: number) {
  if (seconds) toneLen.value = String(seconds);
  toneId = id;
  toneSel.value = id;
  if (id === "file") {
    if (!fileSource) return;
    source = fileSource;
  } else source = makeTone(id, Number(toneLen.value) || 10);
  $("sourceInfo").textContent = `${source.name} · ${duration(source).toFixed(1)} s · ${source.channels.length} ch`;
  player.stop();
  compute();
}
toneSel.onchange = () => setTone(toneSel.value as ToneId | "file");
toneLen.onchange = () => { if (toneId !== "file") setTone(toneId); };
$<HTMLInputElement>("file").onchange = async (e) => {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (!f) return;
  setStatus(`decoding ${f.name}…`, true);
  try {
    fileSource = await decodeFile(f);
    setTone("file");
  } catch (err) {
    setStatus(`could not decode ${f.name}: ${err}`);
  }
};

// live lathe
const live = new LivePanel($("tab-live"), $<HTMLCanvasElement>("latheCanvas"), {
  mode: () => mode,
  values: () => values[mode],
  source: () => source,
  onDubplate: (src) => {
    fileSource = src;
    const opt = [...toneSel.options].find((o) => o.value === "file");
    if (opt) opt.text = `— ${src.name} —`;
    setTone("file");
  },
});

// tabs
document.querySelectorAll<HTMLButtonElement>("#tabs button").forEach((b) => (b.onclick = () => {
  document.querySelectorAll<HTMLButtonElement>("#tabs button").forEach((x) => x.classList.toggle("on", x === b));
  // on narrow screens the parameter panel is a tab of its own
  document.body.classList.toggle("show-params", b.dataset.t === "params");
  if (b.dataset.t === "params") {
    $("tab-compare").hidden = $("tab-live").hidden = $("tab-learn").hidden = true;
    return;
  }
  $("tab-compare").hidden = b.dataset.t !== "compare";
  $("tab-live").hidden = b.dataset.t !== "live";
  $("tab-learn").hidden = b.dataset.t !== "learn";
  latheShown = b.dataset.t === "live";
  live.setVisible(latheShown);
  if (latheShown) {
    $("canvas2d").hidden = true;
    $("view3d").hidden = true;
    $("latheCanvas").hidden = false;
  } else render(false);
}));
renderLearn($("tab-learn"), (f: Finding) => {
  const t = f.try!;
  if (t.mode && t.mode !== mode) {
    mode = t.mode;
  }
  values[mode] = { ...defaults(mode), ...(t.set ?? {}) };
  if (t.monoSum !== undefined) {
    $<HTMLInputElement>("monoSum").checked = t.monoSum;
    player.monoSum = t.monoSum;
  }
  if (t.tone) {
    toneId = t.tone;
    toneSel.value = t.tone;
    toneLen.value = String(t.toneLen ?? 10);
    source = makeTone(t.tone, t.toneLen ?? 10);
    $("sourceInfo").textContent = `${source.name} · ${duration(source).toFixed(1)} s · ${source.channels.length} ch`;
  }
  if (f.title.startsWith("Centre fixed")) view = "both";
  setMode(mode);
  setView(view);
  (document.querySelector('#tabs button[data-t="compare"]') as HTMLButtonElement).click();
});
void FINDINGS;

window.addEventListener("resize", () => last && renderSpectrum(last));
// leaving the narrow layout while the Params tab is open would leave the right panel empty
matchMedia("(max-width: 860px), (max-height: 500px) and (pointer: coarse)").addEventListener("change", (e) => {
  if (!e.matches && document.body.classList.contains("show-params"))
    (document.querySelector('#tabs button[data-t="compare"]') as HTMLButtonElement).click();
});
$("sourceInfo").textContent = `${source.name} · ${duration(source).toFixed(1)} s · ${source.channels.length} ch`;
setMode("spiral");
